import React, { useCallback, useMemo } from 'react';
import { Collapse, Descriptions, Tag, Button, Typography, Row, Col, message, Space } from 'antd';
import { UserOutlined, FileTextOutlined, TableOutlined, DownloadOutlined, PaperClipOutlined } from '@ant-design/icons';
import { ISapSupplier, IApprovalProccessId } from '../../../types';
import SupplierPreviewCard from './SupplierPreviewCard';
import ApprovalInfo from '../../../atoms/approval-info';
import { exportStyledExcel, IExcelExportColumn } from '../../shared/utils/exportStyledExcel';

const { Panel } = Collapse;
const { Text, Link } = Typography;

interface ISapSanitizedSupplier extends Omit<ISapSupplier, 'approval_process_id'> {
    approval_process_id?: {
        status: number;
        data: IApprovalProccessId;
    };
}

interface Props {
    data: ISapSanitizedSupplier;
    attachmentUrls?: Record<string, string>;
}

const DetailSection: React.FC<{ title: React.ReactNode; icon: React.ReactNode; children: React.ReactNode; extra?: React.ReactNode }> = ({ title, icon, children, extra }) => {
    return (
        <Collapse defaultActiveKey={['1']} ghost style={{ marginBottom: 24, background: '#fff', borderRadius: 8 }}>
            <Panel header={<span>{icon} <span style={{ marginLeft: 8 }}>{title}</span></span>} key="1" extra={extra}>
                {children}
            </Panel>
        </Collapse>
    );
};

const SapSupplierDetailView: React.FC<Props> = ({ data, attachmentUrls = {} }) => {
    const exportColumns = useMemo(
        () =>
            (data.columns || [])
                .filter((column): column is NonNullable<typeof column> & { key: string; label: string } => Boolean(column?.key && column?.label))
                .map<IExcelExportColumn<Record<string, unknown>>>(column => ({
                    key: column.key,
                    label: column.label,
                })),
        [data.columns],
    );
    const exportRows = useMemo(() => data.payload || [], [data.payload]);

    const getStatusColor = useCallback((status: string) => {
        switch (status) {
            case "Approved":
                return "green"
            case "Reject":
            case 'Cancel':
                return "red"
            case 'Waiting Approval':
                return "blue"
            default:
                return "blue"
        }
    }, [])

    const handleExport = useCallback(async () => {
        try {
            await exportStyledExcel({
                filename: `SAP_Supplier_${data.record_id || 'detail'}.xlsx`,
                sheetName: 'Supplier Data',
                title: `SAP Supplier - ${data.record_id || '-'}`,
                columns: exportColumns,
                rows: exportRows as Record<string, unknown>[],
            })
        } catch (_error) {
            void message.error('Failed to export supplier Excel')
        }
    }, [data.record_id, exportColumns, exportRows])

    const handleCopyLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(window.location.href)
            void message.success("Supplier link copied")
        } catch (_error) {
            void message.error("Failed to copy supplier link")
        }
    }, [])

    return (
        <>
            <Row gutter={24}>
                <Col xs={24}>
                    <DetailSection title="User Information" icon={<UserOutlined />}>
                        <Descriptions column={{ xs: 1, sm: 2 }}>
                            <Descriptions.Item label="Employee ID">{data.employee_id}</Descriptions.Item>
                            <Descriptions.Item label="Fullname">{data.fullname}</Descriptions.Item>
                            <Descriptions.Item label="Level">{data.level || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Department">{data.department || '-'}</Descriptions.Item>
                        </Descriptions>
                    </DetailSection>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col xs={24}>
                    <DetailSection title="Request Details" icon={<FileTextOutlined />}>
                        <Descriptions column={{ xs: 1, sm: 2 }}>
                            <Descriptions.Item label="Record ID">
                                {data.record_id ? (
                                    <Space size={8}>
                                        <Text>{data.record_id}</Text>
                                        <Link onClick={() => void handleCopyLink()}>Click link to copy</Link>
                                    </Space>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Type of Request">{data.type_of_request}</Descriptions.Item>
                            <Descriptions.Item label="Approval Status">
                                <Tag color={getStatusColor(data.approval_status)}>
                                    {data.approval_status || '-'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Note">
                                {data.note || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Created At">
                                {data.created_at ? new Date(data.created_at).toLocaleString() : '-'}
                            </Descriptions.Item>
                        </Descriptions>
                    </DetailSection>
                </Col>
            </Row>
            {data.attachments && data.attachments.length > 0 && (
                <Row gutter={24}>
                    <Col xs={24}>
                        <DetailSection title="Attachments" icon={<PaperClipOutlined />}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {data.attachments.map((attachment, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                                        <div style={{ width: 32, height: 32, background: '#e6f7ff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <PaperClipOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <Text style={{ display: 'block', fontWeight: 500 }}>{attachment.file_name || `Attachment ${index + 1}`}</Text>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {attachment.description || 'No description'}
                                                {attachment.file_size && ` • ${(attachment.file_size / 1024).toFixed(2)} KB`}
                                            </Text>
                                        </div>
                                        {attachment.file_key && attachmentUrls[attachment.file_key] && (
                                            <Button 
                                                type="link" 
                                                size="small" 
                                                icon={<DownloadOutlined />} 
                                                href={attachmentUrls[attachment.file_key]} 
                                                target="_blank"
                                            >
                                                Download
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </DetailSection>
                    </Col>
                </Row>
            )}

            <Row gutter={24}>
                <Col xs={24}>
                    <DetailSection
                        title="Supplier Data"
                        icon={<TableOutlined />}
                        extra={exportRows.length ? (
                            <div onClick={event => event.stopPropagation()}>
                                <Button icon={<DownloadOutlined />} onClick={() => void handleExport()}>
                                    Export Data Updated
                                </Button>
                            </div>
                        ) : undefined}
                    >
                        <SupplierPreviewCard columns={data.columns || []} items={data.payload || []} />
                    </DetailSection>
                </Col>
            </Row>

            {data.approval_process_id && (
                <Row gutter={24}>
                    <Col xs={24}>
                        <DetailSection title="Approval Info & Comments" icon={<FileTextOutlined />}>
                            <ApprovalInfo documentData={data} usePanel={false} />
                        </DetailSection>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default SapSupplierDetailView;
